import React, { useState, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  FormLayout,
  TextField,
  Select,
  Stack,
  TextContainer,
  Banner,
  Loading,
  Modal,
  Form,
  Text,
  List,
  ButtonGroup,
  Tabs,
  Icon,
  Badge,
  DataTable,
  EmptyState,
  Link,
  Checkbox,
  Heading,
  Toast
} from '@shopify/polaris';
import { DuplicateMinor } from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../contexts/ShopContext';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import GoogleDriveService from '../services/googleDriveService';

// ClickUp Form Component with proper script loading
function ClickUpForm() {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Create and load the ClickUp script
    const script = document.createElement('script');
    script.src = 'https://app-cdn.clickup.com/assets/js/forms-embed/v1.js';
    script.async = true;
    script.onload = () => {
      // Script has loaded, any initialization can happen here if needed
      console.log('ClickUp form script loaded');
    };
    
    document.body.appendChild(script);
    
    // Clean up function to remove the script when component unmounts
    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div ref={containerRef} style={{ minHeight: '800px', width: '100%' }}>
      <iframe 
        ref={iframeRef}
        className="clickup-embed clickup-dynamic-height" 
        src="https://forms.clickup.com/90151310207/f/2kypzmvz-2075/PZJBIEE5SBE84UEG7M" 
        onWheel={() => {}} 
        width="100%" 
        height="100%" 
        style={{ background: 'transparent', border: '1px solid #ccc', minHeight: '800px' }}
      ></iframe>
    </div>
  );
}

export default function Settings() {
  const { user, logout, resetPassword, updatePassword, updateFullName } = useAuth();
  const { currentShop } = useShop();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // User Profile State
  const [userData, setUserData] = useState(null);
  const [showFullNameModal, setShowFullNameModal] = useState(false);
  const [newFullName, setNewFullName] = useState('');

  // Password Change State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Referral System State
  const [selectedTab, setSelectedTab] = useState(0);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [isReferralPartner, setIsReferralPartner] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [showPaypalModal, setShowPaypalModal] = useState(false);
  const [showReferralInfoModal, setShowReferralInfoModal] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [pendingCommissions, setPendingCommissions] = useState(0);
  const [syncingReferrals, setSyncingReferrals] = useState(false);
  
  // Google Drive settings
  const [googleDriveEnabled, setGoogleDriveEnabled] = useState(false);
  const [googleDriveConnected, setGoogleDriveConnected] = useState(false);
  const [googleDriveFolderOption, setGoogleDriveFolderOption] = useState('productName');
  const [googleDriveMainFolder, setGoogleDriveMainFolder] = useState('Editify Product Images');
  const [googleDriveLoading, setGoogleDriveLoading] = useState(false);
  const [googleDriveError, setGoogleDriveError] = useState('');
  
  // Function to refresh user data after Google Drive connection
  const refreshUserData = async () => {
    try {
      if (!user?.uid) return;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        // Load Google Drive settings if they exist
        if (userDoc.data().googleDrive) {
          setGoogleDriveEnabled(userDoc.data().googleDrive.enabled || false);
          setGoogleDriveFolderOption(userDoc.data().googleDrive.folderNamingOption || 'productName');
          setGoogleDriveMainFolder(userDoc.data().googleDrive.mainFolder || 'Editify Product Images');
          setGoogleDriveConnected(userDoc.data().googleDrive.connected || false);
        }
        
        setUserData(userDoc.data());
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
    }
  };
  const [googleDriveSuccess, setGoogleDriveSuccess] = useState('');
  
  // Refs for OAuth popup handling
  const popupTimeoutIdRef = useRef(null);
  const receivedResponseRef = useRef(false);
  
  // Toast notification state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState({ content: '', error: false });
  
  // Define the checkUrlParameters function at component level so it can be used by multiple useEffects
  const checkUrlParameters = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const connected = params.get('googleDriveConnected');
      const success = params.get('googleDriveSuccess');
      const encodedTokens = params.get('tokens');
      const errorMsg = params.get('googleDriveError');
      
      console.log('Checking URL parameters for OAuth completion:', { 
        connected, 
        success,
        hasTokens: encodedTokens ? 'Yes' : 'No',
        errorMsg, 
        fullUrl: window.location.href 
      });
      
      // Handle successful OAuth with tokens
      if (success === 'true' && encodedTokens) {
        console.log('Found googleDriveSuccess=true with tokens in URL parameters');
        
        try {
          // Decode the base64 encoded tokens using browser-compatible method
          // Handle URL-safe base64 encoding (convert back to standard base64 first)
          let base64Str = encodedTokens;
          // Replace URL-safe chars with standard base64 chars
          base64Str = base64Str.replace(/-/g, '+').replace(/_/g, '/');
          // Add padding if needed
          while (base64Str.length % 4) {
            base64Str += '=';
          }
          // Now decode
          const tokensJson = atob(base64Str);
          const tokens = JSON.parse(tokensJson);
          
          console.log('Successfully decoded tokens from URL parameter');
          
          console.log('Successfully decoded tokens:', {
            access_token: tokens.access_token ? 'Present' : 'Missing',
            refresh_token: tokens.refresh_token ? 'Present' : 'Missing',
            expiry_date: tokens.expiry_date
          });
          
          // Save tokens to Firestore using client-side Firebase
          if (user && user.uid) {
            setGoogleDriveLoading(true);
            
            try {
              // Verify authentication before trying to write to Firestore
              const currentUser = getAuth().currentUser;
              if (!currentUser) {
                console.error('Firebase currentUser is null, waiting for auth state to fully initialize');
                // Store tokens temporarily in sessionStorage
                sessionStorage.setItem('pendingGoogleDriveTokens', JSON.stringify(tokens));
                setGoogleDriveError('Please wait while we complete your Google Drive connection...');
                
                // This will be handled by the authentication state observer
                return false;
              }
              
              console.log('User is authenticated, proceeding with token save');
              // Reference to the user's document
              const userDocRef = doc(db, 'users', user.uid);
              
              // Prepare Google Drive data
              const googleDriveData = {
                tokens,
                enabled: true,
                connected: true,
                folderNamingOption: googleDriveFolderOption || 'productName',
                mainFolder: googleDriveMainFolder || 'Editify Product Images',
                lastUpdated: new Date().toISOString()
              };
              
              // Update the user document with Google Drive data
              await updateDoc(userDocRef, {
                googleDrive: googleDriveData
              });
              
              console.log('Successfully saved Google Drive tokens to Firestore');
              
              setGoogleDriveConnected(true);
              setGoogleDriveLoading(false);
              
              setToastMessage({
                content: 'Google Drive connected successfully!',
                error: false
              });
              setToastActive(true);
              
              // Clean up URL parameters after processing
              if (window.history && window.history.replaceState) {
                const cleanUrl = window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
              }
              
              // Close the popup window if this is running in a popup
              if (window.opener) {
                console.log('Detected popup window with success, closing and notifying parent...');
                
                // Get the parent origin from localStorage if available
                let parentOrigin = '*';
                try {
                  const storedOrigin = localStorage.getItem('parentOrigin');
                  if (storedOrigin) {
                    parentOrigin = storedOrigin;
                    console.log('Using stored parent origin for postMessage:', parentOrigin);
                  }
                } catch (e) {
                  console.warn('Could not get parent origin from localStorage:', e);
                }
                
                // Try multiple communication methods to ensure the message gets through
                try {
                  // Method 1: Direct postMessage
                  window.opener.postMessage({ type: 'GOOGLE_DRIVE_CONNECTED', success: true }, parentOrigin);
                  console.log('Sent postMessage to parent window');
                  
                  // Method 2: Try to call parent's callback function directly
                  if (window.opener.googleDriveCallback) {
                    window.opener.googleDriveCallback({ success: true });
                    console.log('Called parent googleDriveCallback function directly');
                  }
                  
                  // Method 3: Store success flag in localStorage as fallback
                  localStorage.setItem('googleDriveConnected', 'true');
                  localStorage.setItem('googleDriveConnectedTime', new Date().toISOString());
                  localStorage.setItem('googleDriveConnectedSource', 'popup-window');
                  localStorage.setItem('googleDriveConnectedUrl', window.location.href);
                  console.log('Stored success in localStorage as fallback');
                } catch (err) {
                  console.error('Error communicating with parent window:', err);
                }
                
                // Force close the window after a short delay to ensure message is sent
                setTimeout(() => {
                  console.log('Closing popup window');
                  window.close();
                }, 500);
              }
              
              setToastMessage({
                content: 'Google Drive connected successfully!',
                error: false
              });
              setToastActive(true);
              
              // Refresh user data to get the complete updated Google Drive settings
              refreshUserData();
            } catch (firestoreError) {
              console.error('Error writing to Firestore:', firestoreError);
              // If we get an UNAUTHENTICATED error, store tokens temporarily
              if (firestoreError.code === 'permission-denied' || 
                  firestoreError.code === 'unauthenticated' ||
                  firestoreError.message?.includes('UNAUTHENTICATED')) {
                console.log('Authentication issue detected, storing tokens temporarily');
                sessionStorage.setItem('pendingGoogleDriveTokens', JSON.stringify(tokens));
                setGoogleDriveError('Authentication in progress. Please wait a moment...');
              } else {
                setGoogleDriveError(`Failed to save Google Drive connection: ${firestoreError.message}`);
                setGoogleDriveLoading(false);
              }
            }
          } else {
            console.error('User not authenticated, cannot save tokens');
            setGoogleDriveError('You must be logged in to connect Google Drive');
            setGoogleDriveLoading(false);
            
            // Store tokens temporarily
            sessionStorage.setItem('pendingGoogleDriveTokens', JSON.stringify(tokens));
          }
        } catch (decodeError) {
          console.error('Error decoding or saving tokens:', decodeError);
          setGoogleDriveError('Failed to process Google Drive connection');
          setGoogleDriveLoading(false);
        }
        
        // Clean up URL parameters
        if (window.history && window.history.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        
        return true;
      }
      
      // Handle legacy success parameter
      if (connected === 'true') {
        console.log('Found googleDriveConnected=true in URL parameters (legacy format)');
        
        // Update app state for successful connection
        setGoogleDriveConnected(true);
        setGoogleDriveLoading(false);
        setGoogleDriveError('');
        
        // Show success toast
        setToastMessage({ 
          content: 'Google Drive connected successfully!', 
          error: false 
        });
        setToastActive(true);
        
        // Refresh user data to get updated Google Drive settings
        refreshUserData();
        
        // Clean up URL parameters
        if (window.history && window.history.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        
        return true;
      }
      
      // Handle error parameter
      else if (errorMsg) {
        console.error('Google Drive OAuth error:', errorMsg);
        setGoogleDriveLoading(false);
        setGoogleDriveError(`Authentication error: ${errorMsg}`);
        
        // Show error toast
        setToastMessage({
          content: `Google Drive authentication failed: ${errorMsg}`,
          error: true
        });
        setToastActive(true);
        
        // Clean up URL parameters without reloading the page
        if (window.history && window.history.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        
        // Close the popup window if this is running in a popup
        if (window.opener) {
          console.log('Detected popup window with error, closing and notifying parent...');
          // Tell the parent window about the error
          try {
            window.opener.postMessage({ type: 'GOOGLE_DRIVE_ERROR', error: errorMsg }, '*');
          } catch (err) {
            console.error('Error sending message to parent window:', err);
          }
          // Force close the window
          setTimeout(() => {
            window.close();
          }, 300);
        }
        
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error checking URL parameters for OAuth completion:', err);
      return false;
    }
  };

  // Function to check localStorage for OAuth completion information
  const checkLocalStorage = () => {
    try {
      const localGoogleDriveInfo = localStorage.getItem('googleDriveOAuthResult');
      if (localGoogleDriveInfo) {
        const parsedInfo = JSON.parse(localGoogleDriveInfo);
        console.log('Found Google Drive info in localStorage:', parsedInfo);
        
        // Only process if we have a success indicator and it's relatively recent (within last 5 minutes)
        if (parsedInfo.success && parsedInfo.timestamp) {
          const timestamp = parseInt(parsedInfo.timestamp, 10);
          const now = Date.now();
          const fiveMinutesAgo = now - (5 * 60 * 1000);
          
          if (timestamp > fiveMinutesAgo) {
            console.log('Processing recent Google Drive OAuth result from localStorage');
            
            // Update app state
            setGoogleDriveConnected(true);
            setGoogleDriveLoading(false);
            setGoogleDriveError('');
            
            // Show success toast
            setToastMessage({ 
              content: 'Google Drive connected successfully (detected via localStorage)!', 
              error: false 
            });
            setToastActive(true);
            
            // Clean up localStorage
            localStorage.removeItem('googleDriveOAuthResult');
            
            // Refresh user data to get updated Google Drive settings
            refreshUserData();
            
            return true;
          } else {
            console.log('Found stale Google Drive OAuth result in localStorage, removing it');
            localStorage.removeItem('googleDriveOAuthResult');
          }
        }
      }
      
      return false;
    } catch (err) {
      console.error('Error checking localStorage for OAuth completion:', err);
      return false;
    }
  };

  // Model token limits
  const modelTokenLimits = {
    'gpt-4': 8192,
    'gpt-4-32k': 32768,
    'gpt-4-1106-preview': 128000,
    'gpt-4o': 128000,
    'gpt-4o-mini': 128000,
    'gpt-3.5-turbo': 4096,
    'gpt-3.5-turbo-16k': 16385,
    'gpt-3.5-turbo-1106': 16385
  };

  // Effect to check for OAuth completion via URL parameters and localStorage
  useEffect(() => {
    // Don't check if user isn't loaded yet
    if (!user) return;
    
    console.log('Checking for OAuth completion on component mount or URL/user change');
    
    // Check URL parameters first, then localStorage if needed
    const checkForOAuthCompletion = async () => {
      const foundUrlParams = await checkUrlParameters();
      if (!foundUrlParams) {
        checkLocalStorage();
      }
    };
    
    // Run check immediately
    checkForOAuthCompletion();
    
    // Also set up a short interval to check a few more times in case of race conditions
    // This helps with popup windows that might close before state is fully updated
    let checkCount = 0;
    const maxChecks = 5;
    const checkInterval = setInterval(() => {
      checkCount++;
      console.log(`Re-checking OAuth completion (attempt ${checkCount}/${maxChecks})`);
      checkForOAuthCompletion();
      
      if (checkCount >= maxChecks) {
        clearInterval(checkInterval);
      }
    }, 1000);
    
    // Clear interval on component unmount
    return () => {
      clearInterval(checkInterval);
    };
  }, [window.location.search, user]);

  // The OAuth checking logic is now handled by the useEffect above

  // Listen for messages from popup windows
  useEffect(() => {
    const handleMessage = (event) => {
      // Check if this is a message from our Google Drive OAuth popup
      if (event.data && event.data.type) {
        console.log('Received message from popup:', event.data);
        
        if (event.data.type === 'GOOGLE_DRIVE_CONNECTED' && event.data.success) {
          // Refresh Google Drive connection status
          console.log('Refreshing Google Drive status after successful connection');
          GoogleDriveService.hasValidCredentials(user?.uid).then(isValid => {
            if (isValid) {
              setGoogleDriveConnected(true);
              setToastMessage({
                content: 'Google Drive connected successfully!',
                error: false
              });
              setToastActive(true);
              // Refresh user data to update UI
              refreshUserData();
            }
          });
        } else if (event.data.type === 'GOOGLE_DRIVE_ERROR') {
          // Show error
          setGoogleDriveError(`Authentication error: ${event.data.error || 'Unknown error'}`);
          setToastMessage({
            content: `Google Drive authentication failed: ${event.data.error || 'Unknown error'}`,
            error: true
          });
          setToastActive(true);
        }
      }
    };
    
    // Add event listener
    window.addEventListener('message', handleMessage);
    
    // Clean up
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user]);

  // Effect to handle pending Google Drive tokens after authentication is fully established
  useEffect(() => {
    if (!user || !user.uid) return;
    
    try {
      // Check if we have pending tokens stored during a previous authentication issue
      const pendingTokensJson = sessionStorage.getItem('pendingGoogleDriveTokens');
      if (pendingTokensJson) {
        console.log('Found pending Google Drive tokens in sessionStorage');
        const tokens = JSON.parse(pendingTokensJson);
        
        // Remove from sessionStorage immediately to prevent multiple attempts
        sessionStorage.removeItem('pendingGoogleDriveTokens');
        
        // Double check authentication
        const currentUser = getAuth().currentUser;
        if (!currentUser) {
          console.error('User still not fully authenticated, cannot save pending tokens');
          return;
        }
        
        // Save the pending tokens
        setGoogleDriveLoading(true);
        
        // Reference to the user's document
        const userDocRef = doc(db, 'users', user.uid);
        
        // Prepare Google Drive data
        const googleDriveData = {
          tokens,
          enabled: true,
          connected: true,
          folderNamingOption: googleDriveFolderOption || 'productName',
          mainFolder: googleDriveMainFolder || 'Editify Product Images',
          lastUpdated: new Date().toISOString()
        };
        
        // Update the user document with Google Drive data
        (async () => {
          try {
            await updateDoc(userDocRef, {
              googleDrive: googleDriveData
            });
            
            console.log('Successfully saved pending Google Drive tokens to Firestore');
            
            // Update local state
            setGoogleDriveConnected(true);
            setGoogleDriveLoading(false);
            setGoogleDriveError('');
            
            // Show success toast
            setToastMessage({
              content: 'Google Drive connected successfully!',
              error: false
            });
            setToastActive(true);
            
            // Refresh user data
            refreshUserData();
          } catch (error) {
            console.error('Failed to save pending tokens:', error);
            setGoogleDriveError(`Failed to complete Google Drive connection: ${error.message}`);
            setGoogleDriveLoading(false);
          }
        })();
      }
    } catch (error) {
      console.error('Error processing pending tokens:', error);
    }
  }, [user]);

  // Load user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!user?.uid) return;
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
          if (userDoc.data().fullName) {
            setNewFullName(userDoc.data().fullName);
          }
          
          // Check if user is a referral partner
          if (userDoc.data().isReferralPartner) {
            setIsReferralPartner(true);
            setReferralCode(userDoc.data().partnerReferralCode || '');
            fetchReferralInfo();
          } else if (userDoc.data().referralCode) {
            // User has a referral code but is not a partner (they were referred by someone)
            setReferralCode(userDoc.data().referralCode || '');
          }
          
          // Load Google Drive settings if they exist
          if (userDoc.data().googleDrive) {
            setGoogleDriveEnabled(userDoc.data().googleDrive.enabled || false);
            setGoogleDriveFolderOption(userDoc.data().googleDrive.folderNamingOption || 'productName');
            setGoogleDriveMainFolder(userDoc.data().googleDrive.mainFolder || 'Editify Product Images');
            setGoogleDriveConnected(!!(userDoc.data().googleDrive.tokens && userDoc.data().googleDrive.tokens.access_token));
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [user]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/app/login');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to log out');
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    try {
      if (!user?.email) {
        throw new Error('No email address available');
      }

      setLoading(true);
      setError('');
      await resetPassword(user.email);
      setSuccess('Password reset email sent. Please check your inbox.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    try {
      setError('');
      setLoading(true);

      // Validation
      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters');
      }

      await updatePassword(currentPassword, newPassword);
      
      // Clear form and show success
      setSuccess('Password updated successfully');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Change password error:', error);
      setError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle full name update
  const handleUpdateFullName = async () => {
    try {
      setError('');
      setLoading(true);

      if (!newFullName.trim()) {
        throw new Error('Full name cannot be empty');
      }

      await updateFullName(newFullName);
      
      // Update local state
      if (userData) {
        setUserData({
          ...userData,
          fullName: newFullName
        });
      }
      
      // Clear form and show success
      setSuccess('Full name updated successfully');
      setShowFullNameModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Update full name error:', error);
      setError(error.message || 'Failed to update full name');
    } finally {
      setLoading(false);
    }
  };

  // Update PayPal email for referral partner
  const handleUpdatePaypalEmail = async () => {
    try {
      setLoading(true);
      
      if (!user?.uid) {
        throw new Error('You must be logged in to update your PayPal email');
      }
      
      // Fetch the latest referral partner information
      const referralSystemRef = doc(db, 'referralSystem', user.uid);
      const referralDoc = await getDoc(referralSystemRef);
      
      if (!referralDoc.exists()) {
        throw new Error('You are not registered as a referral partner');
      }
      
      // Update the referral partner document with the PayPal email
      await updateDoc(referralSystemRef, {
        paypalEmail: paypalEmail,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setReferralInfo(prev => ({
        ...prev,
        paypalEmail: paypalEmail
      }));
      
      setSuccess('PayPal email updated successfully');
      setShowPaypalModal(false);
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh referral info
      fetchReferralInfo();
    } catch (error) {
      console.error('Error updating PayPal email:', error);
      setError(error.message || 'Failed to update PayPal email');
    } finally {
      setLoading(false);
    }
  };

  // Fetch referral information and referred users
  const fetchReferralInfo = async () => {
    try {
      if (!user?.uid) return;
      
      setLoadingReferrals(true);
      
      // First check if the user is a referral partner in the users collection
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const isUserAdmin = userData.role === 'admin';
      
      if (!userData.isReferralPartner) {
        setIsReferralPartner(false);
        setLoadingReferrals(false);
        return;
      }
      
      // User is a referral partner, prioritize the centralized referral system
      let partnerData = {};
      let referredUsersData = [];
      
      try {
        const referralSystemDoc = await getDoc(doc(db, 'referralSystem', user.uid));
        
        // If we have data in the centralized system, use it
        if (referralSystemDoc.exists()) {
          console.log('Found referral system data for partner');
          const referralSystemData = referralSystemDoc.data();
          
          partnerData = {
            id: user.uid,
            code: referralSystemData.code || userData.partnerReferralCode,
            commission: referralSystemData.commission || 10,
            active: referralSystemData.isActive !== false,
            referralCount: referralSystemData.referralStats?.total || 0,
            paypalEmail: referralSystemData.paypalEmail || userData.paypalEmail,
            createdAt: referralSystemData.createdAt,
            updatedAt: referralSystemData.updatedAt,
            partnerName: referralSystemData.partnerName || userData.fullName || '',
            partnerEmail: referralSystemData.partnerEmail || userData.email || ''
          };
          
          // Get referred users from the centralized system
          if (referralSystemData.referredUsers && referralSystemData.referredUsers.length > 0) {
            console.log(`Found ${referralSystemData.referredUsers.length} referred users in the centralized system`);
            referredUsersData = referralSystemData.referredUsers.map(ru => ({
              id: ru.userId,
              email: ru.email,
              fullName: ru.fullName,
              createdAt: ru.registeredAt,
              activatedAt: ru.activatedAt,
              subscription: ru.subscription || null
            }));
          }
        } else {
          // If no data in centralized system, check users collection for referral code
          console.log('No data in centralized system, checking user document');
          
          partnerData = {
            id: user.uid,
            code: userData.partnerReferralCode || '',
            commission: userData.referralCommission || 10,
            active: true,
            referralCount: 0,
            paypalEmail: userData.paypalEmail || '',
            createdAt: userData.createdAt,
            updatedAt: userData.updatedAt,
            partnerName: userData.fullName || '',
            partnerEmail: userData.email || ''
          };
          
          // Create the referral system document if it doesn't exist
          if (partnerData.code) {
            console.log('Creating referral system document for partner');
            try {
              await setDoc(doc(db, 'referralSystem', user.uid), {
                partnerId: user.uid,
                partnerName: userData.fullName || '',
                partnerEmail: userData.email || '',
                code: partnerData.code,
                commission: partnerData.commission,
                isActive: true,
                referredUsers: [],
                referralStats: { total: 0, active: 0 },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
            } catch (error) {
              console.error('Error creating referral system document:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching from referral system:', error);
        // Continue with user data only
        partnerData = {
          id: user.uid,
          code: userData.partnerReferralCode || '',
          commission: userData.referralCommission || 10,
          active: true,
          referralCount: 0,
          paypalEmail: userData.paypalEmail || '',
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt,
          partnerName: userData.fullName || '',
          partnerEmail: userData.email || ''
        };
      }
      
      // If we still don't have referred users, check users collection
      if (referredUsersData.length === 0 && partnerData.code) {
        console.log('Checking users collection for referred users');
        try {
          const usersRef = collection(db, 'users');
          const referredUsersQuery = query(usersRef, where('referrerId', '==', user.uid));
          const usersSnapshot = await getDocs(referredUsersQuery);
          
          if (!usersSnapshot.empty) {
            console.log(`Found ${usersSnapshot.docs.length} referred users in users collection`);
            const usersData = usersSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Format the user data
            referredUsersData = usersData.map(userData => ({
              id: userData.id,
              email: userData.email || '',
              fullName: userData.fullName || '',
              createdAt: userData.createdAt || '',
              activatedAt: userData.updatedAt || '',
              subscription: userData.subscription || null
            }));
            
            // Update the referral system document with these users
            if (referredUsersData.length > 0) {
              console.log('Updating referral system with found users');
              try {
                const referralSystemRef = doc(db, 'referralSystem', user.uid);
                await updateDoc(referralSystemRef, {
                  referredUsers: referredUsersData.map(ru => ({
                    userId: ru.id,
                    email: ru.email,
                    fullName: ru.fullName,
                    registeredAt: ru.createdAt,
                    activatedAt: ru.activatedAt,
                    subscription: ru.subscription
                  })),
                  referralStats: { 
                    total: referredUsersData.length, 
                    active: referredUsersData.length 
                  },
                  updatedAt: new Date().toISOString()
                });
              } catch (updateError) {
                console.error('Error updating referral system document:', updateError);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching referred users from users collection:', error);
        }
      }
      
      // Set the partner data and referred users
      setReferralInfo(partnerData);
      setIsReferralPartner(true);
      setReferralCode(partnerData.code || '');
      
      // Set PayPal email if available
      if (partnerData.paypalEmail) {
        setPaypalEmail(partnerData.paypalEmail);
      }
      
      setReferredUsers(referredUsersData);
      
      // Calculate pending commissions safely
      let totalEarnedAmount = 0;
      try {
        referredUsersData.forEach(user => {
          if (user.subscription && user.subscription.active) {
            // Determine subscription date using fallbacks
            let subscriptionDate = null;
            if (user.subscription.startDate) {
              subscriptionDate = new Date(user.subscription.startDate);
            } else if (user.subscription.createdAt) {
              subscriptionDate = new Date(user.subscription.createdAt);
            } else if (user.activatedAt) {
              subscriptionDate = new Date(user.activatedAt);
            } else if (user.registeredAt) {
              subscriptionDate = new Date(user.registeredAt);
            } else {
              // If no date is available, use a date 60 days ago
              const fallbackDate = new Date();
              fallbackDate.setDate(fallbackDate.getDate() - 60);
              subscriptionDate = fallbackDate;
              console.log('Using fallback date for subscription:', fallbackDate);
            }
            
            // Calculate commission based on actual subscription plan price
            const planPrice = user.subscription.planPrice || 0;
            const commissionRate = partnerData.commission / 100;
            
            // Add base subscription commission
            totalEarnedAmount += planPrice * commissionRate;
            
            // Add renewal commissions if they exist
            if (user.renewals && Array.isArray(user.renewals)) {
              // Add commission for each renewal
              user.renewals.forEach(renewal => {
                const renewalPrice = renewal.planPrice || planPrice;
                totalEarnedAmount += renewalPrice * commissionRate;
              });
            }
          }
        });
      } catch (error) {
        console.error('Error calculating commissions:', error);
      }
      
      // Calculate total paid amount from payout history
      let totalPaidAmount = 0;
      try {
        const payoutsRef = collection(db, 'referralPayouts');
        const payoutsQuery = query(payoutsRef, where('partnerId', '==', user.uid), where('status', '==', 'completed'));
        const payoutsSnapshot = await getDocs(payoutsQuery);
        
        payoutsSnapshot.docs.forEach(doc => {
          const payout = doc.data();
          totalPaidAmount += parseFloat(payout.amount) || 0;
        });
      } catch (error) {
        console.error('Error calculating total paid amount:', error);
      }
      
      // Calculate pending amount by subtracting paid amount from total earned
      const pendingAmount = Math.max(0, totalEarnedAmount - totalPaidAmount);
      setPendingCommissions(pendingAmount);
      
      // Fetch payout history
      try {
        const payoutsRef = collection(db, 'referralPayouts');
        const payoutsQuery = query(payoutsRef, where('partnerId', '==', user.uid));
        const payoutsSnapshot = await getDocs(payoutsQuery);
        
        const payoutHistoryData = payoutsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by date, newest first
        payoutHistoryData.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        setPayoutHistory(payoutHistoryData);
      } catch (error) {
        console.error('Error fetching payout history:', error);
        setPayoutHistory([]);
      }
    } catch (error) {
      console.error('Error fetching referral information:', error);
    } finally {
      setLoadingReferrals(false);
    }
  };

  // Copy referral link to clipboard
  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setSuccess('Referral link copied to clipboard!');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };

  // Sync referred users' subscription data
  const syncReferredUsersSubscriptions = async () => {
    try {
      setSyncingReferrals(true);
      console.log('Starting manual sync of referred users subscriptions...');
      
      // Get all users who were referred by this partner
      const usersRef = collection(db, 'users');
      const referredUsersQuery = query(usersRef, where('referrerId', '==', user.uid));
      const referredUsersSnapshot = await getDocs(referredUsersQuery);
      
      if (referredUsersSnapshot.empty) {
        console.log('No referred users found');
        alert('No referred users found to sync');
        setSyncingReferrals(false);
        return;
      }
      
      console.log(`Found ${referredUsersSnapshot.docs.length} referred users to sync`);
      
      // Get the referral system document
      const referralSystemRef = doc(db, 'referralSystem', user.uid);
      const referralSystemDoc = await getDoc(referralSystemRef);
      
      if (!referralSystemDoc.exists()) {
        console.log('No referral system document found, creating one');
        // Create the referral system document if it doesn't exist
        await setDoc(doc(db, 'referralSystem', user.uid), {
          partnerId: user.uid,
          partnerName: userData.fullName || '',
          partnerEmail: userData.email || '',
          code: userData.partnerReferralCode || referralCode,
          commission: userData.referralCommission || 10,
          isActive: true,
          referredUsers: [],
          referralStats: { total: 0, active: 0 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      // Process each referred user
      const referredUsersData = [];
      let activeCount = 0;
      
      for (const userDoc of referredUsersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Get the user's subscription data
        const subscription = userData.subscription || null;
        
        // Format subscription data
        const formattedSubscription = subscription ? {
          active: subscription.status === 'active',
          planType: subscription.type || null,
          planName: subscription.planName || null,
          planPrice: subscription.price || 0,
          startDate: subscription.startDate || null,
          cancelDate: subscription.cancelDate || null,
          status: subscription.status || 'inactive'
        } : {
          active: false,
          status: 'inactive'
        };
        
        // Add to referred users array
        referredUsersData.push({
          userId: userId,
          email: userData.email || '',
          fullName: userData.fullName || '',
          registeredAt: userData.createdAt || new Date().toISOString(),
          activatedAt: formattedSubscription.active ? userData.updatedAt || new Date().toISOString() : null,
          subscription: formattedSubscription
        });
        
        // Count active subscriptions
        if (formattedSubscription.active) {
          activeCount++;
        }
      }
      
      // Update the referral system document
      await updateDoc(referralSystemRef, {
        referredUsers: referredUsersData,
        referralStats: { 
          total: referredUsersData.length, 
          active: activeCount 
        },
        updatedAt: new Date().toISOString()
      });
      
      console.log('Successfully synced referred users subscriptions');
      alert('Successfully synced referred users data');
      
      // Refresh the referral information
      await fetchReferralInfo();
    } catch (error) {
      console.error('Error syncing referred users subscriptions:', error);
      alert('Error syncing referred users data: ' + error.message);
    } finally {
      setSyncingReferrals(false);
    }
  };

  return (
    <Page title="Settings">
      {loading && <Loading />}
      
      {error && (
        <Banner status="critical" onDismiss={() => setError('')}>
          <p>{error}</p>
        </Banner>
      )}
      
      {success && (
        <Banner status="success" onDismiss={() => setSuccess('')}>
          <p>{success}</p>
        </Banner>
      )}

      <Layout>
        <Layout.Section>
          <Tabs
            tabs={[
              {
                id: 'account',
                content: 'Account Settings',
                accessibilityLabel: 'Account Settings',
                panelID: 'account-settings-panel',
              },
              // AI Provider Settings tab removed - now using centralized API key management
              {
                id: 'referral',
                content: 'Referral Program',
                accessibilityLabel: 'Referral Program',
                panelID: 'referral-program-panel',
              },
              {
                id: 'help',
                content: 'Help & Resources',
                accessibilityLabel: 'Help & Resources',
                panelID: 'help-resources-panel',
              },
              {
                id: 'externalSpaces',
                content: 'External Spaces',
                accessibilityLabel: 'External Spaces',
                panelID: 'external-spaces-panel',
              },
              {
                id: 'reportBug',
                content: 'Report a Bug',
                accessibilityLabel: 'Report a Bug',
                panelID: 'report-bug-panel',
              },
            ]}
            selected={selectedTab}
            onSelect={(index) => setSelectedTab(index)}
          />
          
          {selectedTab === 0 && (
            <Card title="Account Settings" sectioned>
              <Card.Section title="User Profile">
                <Stack vertical spacing="loose">
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack vertical spacing="tight">
                      <TextContainer>
                        <Text variant="bodyMd" as="p">
                          <strong>Email:</strong> {user?.email}
                        </Text>
                        <Text variant="bodyMd" as="p">
                          <strong>Full Name:</strong> {userData?.fullName || 'Not set'}
                        </Text>
                      </TextContainer>
                    </Stack>
                    <Button onClick={() => setShowFullNameModal(true)}>
                      Update Full Name
                    </Button>
                  </Stack>
                </Stack>
              </Card.Section>
              
              <Card.Section title="Password Management">
                <Stack vertical spacing="loose">
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack vertical spacing="tight">
                      <TextContainer>
                        <Text variant="bodyMd" as="p">
                          Change your password or reset it if forgotten.
                        </Text>
                      </TextContainer>
                    </Stack>
                    <Stack>
                      <Button onClick={() => setShowPasswordModal(true)}>
                        Change Password
                      </Button>
                      <Button onClick={handlePasswordReset}>
                        Reset Password
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Card.Section>
              
              <Card.Section title="Account Actions">
                <Stack distribution="equalSpacing" alignment="center">
                  <Text variant="bodyMd" as="p">
                    Sign out of your account.
                  </Text>
                  <ButtonGroup>
                    <Button onClick={handleLogout} destructive>
                      Logout
                    </Button>
                    <Button onClick={() => window.location.href = 'mailto:support@geteditify.com?subject=Support%20for%20Shopify%20Product%20Editor%20Pro'}>
                      Support
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Card.Section>
            </Card>
          )}
          
          {/* AI Provider Settings tab and content completely removed - now using centralized API key management */}
          
          {selectedTab === 1 && (
            <Card title="Referral Program" sectioned>
              {isReferralPartner ? (
                <>
                  <Card.Section title="Your Referral Link">
                    <Stack vertical spacing="loose">
                      <div style={{ width: '100%' }}>
                        <TextField
                          label="Share this link with others"
                          value={`${window.location.origin}/register?ref=${referralCode}`}
                          readOnly
                          connectedRight={
                            <Button onClick={copyReferralLink} icon={DuplicateMinor}>
                              Copy
                            </Button>
                          }
                        />
                      </div>
                      <Banner status="info" fullWidth>
                        <p>When someone signs up using your referral link, you'll earn a commission on their subscription!</p>
                      </Banner>
                    </Stack>
                  </Card.Section>
                  
                  <Card.Section title="Referred Users">
                    {loadingReferrals ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Loading />
                      </div>
                    ) : (
                      <>
                        {referredUsers.length > 0 ? (
                          <div style={{ marginTop: '20px' }}>
                            <DataTable
                              columnContentTypes={['text', 'text', 'text', 'text']}
                              headings={['User', 'Joined Date', 'Subscription Status', 'Commission']}
                              rows={referredUsers.map(user => [
                                user.email,
                                user.createdAt ? (typeof user.createdAt === 'object' && user.createdAt.seconds ? 
                                  new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 
                                  new Date(user.createdAt).toLocaleDateString()) : 'Unknown',
                                user.subscription && user.subscription.active ? (
                                  <Badge status="success">Active</Badge>
                                ) : (
                                  <Badge>Inactive</Badge>
                                ),
                                user.subscription && user.subscription.active ? 
                                  `$${((user.subscription.planPrice) * (referralInfo?.commission / 100)).toFixed(2)}` : 
                                  '$0.00'
                              ])}
                            />
                          </div>
                        ) : (
                          <div style={{ marginTop: '20px' }}>
                            <EmptyState
                              heading="No referrals yet"
                              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                            >
                              <p>Share your referral link to start earning commissions!</p>
                            </EmptyState>
                          </div>
                        )}
                      </>
                    )}
                  </Card.Section>
                  
                  <Card.Section title="Referral Statistics">
                    {loadingReferrals ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Loading />
                      </div>
                    ) : (
                      <>
                        <Stack distribution="equalSpacing" alignment="center" spacing="loose">
                          <Stack vertical spacing="tight">
                            <TextContainer>
                              <Text variant="headingMd" as="h3">{referredUsers.length}</Text>
                              <Text variant="bodyMd" as="p">Total Referred Users</Text>
                            </TextContainer>
                          </Stack>
                          <Stack vertical spacing="tight">
                            <TextContainer>
                              <Text variant="headingMd" as="h3">
                                {referredUsers.filter(user => user.subscription && user.subscription.active).length}
                              </Text>
                              <Text variant="bodyMd" as="p">Active Subscriptions</Text>
                            </TextContainer>
                          </Stack>
                          <Stack vertical spacing="tight">
                            <TextContainer>
                              <Text variant="headingMd" as="h3">{referralInfo?.commission || 0}%</Text>
                              <Text variant="bodyMd" as="p">Commission Rate</Text>
                            </TextContainer>
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </Card.Section>
                  
                  <Card.Section title="Commission & Payouts">
                    <Stack vertical spacing="loose">
                      <Stack distribution="equalSpacing" alignment="center">
                        <Stack vertical spacing="tight">
                          <TextContainer>
                            <Text variant="headingMd" as="h3">${pendingCommissions.toFixed(2)}</Text>
                            <Text variant="bodyMd" as="p">Pending Commission</Text>
                          </TextContainer>
                        </Stack>
                        <Stack vertical spacing="tight">
                          <TextContainer>
                            <Text variant="headingMd" as="h3">
                              ${payoutHistory.length > 0 ? payoutHistory[0].amount.toFixed(2) : '0.00'}
                            </Text>
                            <Text variant="bodyMd" as="p">Last Payout</Text>
                          </TextContainer>
                        </Stack>
                        <Stack vertical spacing="tight">
                          <TextContainer>
                            <Text variant="headingMd" as="h3">
                              ${payoutHistory.reduce((total, payout) => total + payout.amount, 0).toFixed(2)}
                            </Text>
                            <Text variant="bodyMd" as="p">Total Earned</Text>
                          </TextContainer>
                        </Stack>
                      </Stack>
                      
                      <Banner status="info" fullWidth>
                        <p>Commissions are calculated based on active subscriptions from your referred users. Payouts are processed monthly.</p>
                      </Banner>
                      
                      {payoutHistory.length > 0 ? (
                        <>
                          <Text variant="headingMd" as="h3">Payout History</Text>
                          <DataTable
                            columnContentTypes={[
                              'text',
                              'numeric',
                              'text',
                              'text'
                            ]}
                            headings={[
                              'Date',
                              'Amount',
                              'Status',
                              'Transaction ID'
                            ]}
                            rows={payoutHistory.map(payout => [
                              new Date(payout.date).toLocaleDateString(),
                              `$${payout.amount.toFixed(2)}`,
                              <Badge status={payout.status === 'completed' ? 'success' : 'attention'}>
                                {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                              </Badge>,
                              payout.transactionId || '-'
                            ])}
                          />
                        </>
                      ) : (
                        <EmptyState
                          heading="No payout history yet"
                          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                        >
                          <p>Your payout history will appear here once you start earning commissions.</p>
                        </EmptyState>
                      )}
                    </Stack>
                  </Card.Section>
                  <Card.Section title="Payout Settings">
                    <Stack vertical spacing="loose">
                      <TextField
                        label="PayPal Email"
                        value={paypalEmail}
                        readOnly
                        helpText="Enter your PayPal email address to receive payouts"
                      />
                      <Button onClick={() => setShowPaypalModal(true)}>
                        Update PayPal Email
                      </Button>
                    </Stack>
                  </Card.Section>
                </>
              ) : (
                <Card.Section>
                  {referralCode && !isReferralPartner ? (
                    <Banner status="info">
                      <p>You were referred by a partner with code: <strong>{referralCode}</strong></p>
                    </Banner>
                  ) : null}
                  
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <EmptyState
                      heading="Become a Referral Partner"
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                      action={{
                        content: 'Apply to Join',
                        onAction: () => setShowReferralInfoModal(true)
                      }}
                    >
                      <p>Interested in becoming a referral partner? Learn more about our program and apply to join!</p>
                    </EmptyState>
                  </div>
                </Card.Section>
              )}
            </Card>
          )}
          {selectedTab === 2 && (
            <Card title="Help & Resources" sectioned>
              <Card.Section>
                <Stack vertical spacing="tight">
                  <Text variant="bodyMd" as="p">
                    Learn how to make the most of Editify.
                  </Text>
                  <List>
                    <List.Item>
                      Create and manage AI-powered product templates
                    </List.Item>
                    <List.Item>
                      Bulk edit product titles and descriptions
                    </List.Item>
                    <List.Item>
                      Optimize your product content for better visibility
                    </List.Item>
                  </List>
                  <ButtonGroup>
                    <Button url="https://splendid-nape-e37.notion.site/Shopify-Product-Editor-Pro-19546db4a51980678977fe6dd71014f3" external>
                      Documentation
                    </Button>
                    <Button onClick={() => window.location.href = 'mailto:support@geteditify.com?subject=Support%20for%20Editify'}>
                      Support
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Card.Section>
            </Card>
          )}
          {selectedTab === 3 && (
            <Card title="External Spaces" sectioned>
              <Card.Section title="Google Drive Integration">
                {googleDriveLoading && <Loading />}
                
                {googleDriveError && (
                  <Banner status="critical" onDismiss={() => setGoogleDriveError('')}>
                    <p>{googleDriveError}</p>
                  </Banner>
                )}
                
                {googleDriveSuccess && (
                  <Banner status="success" onDismiss={() => setGoogleDriveSuccess('')}>
                    <p>{googleDriveSuccess}</p>
                  </Banner>
                )}
                
                <Stack vertical spacing="loose">
                  <TextContainer>
                    <p>
                      Connect your Google Drive account to automatically upload processed images as a backup and for copyright protection.
                      Images will be uploaded to Google Drive after being pushed to Shopify.
                    </p>
                  </TextContainer>
                  <TextContainer>
                    <p><strong>Note:</strong> You can disconnect your Google Drive account at any time by clicking the "Disconnect Google Drive" button.</p>
                  </TextContainer>
                  
                  <Checkbox
                    label="Enable Google Drive Upload"
                    checked={googleDriveEnabled}
                    disabled={!googleDriveConnected}
                    onChange={async (checked) => {
                      setGoogleDriveEnabled(checked);
                      try {
                        setGoogleDriveLoading(true);
                        const result = await GoogleDriveService.saveSettings(user.uid, {
                          enabled: checked,
                          folderNamingOption: googleDriveFolderOption,
                          mainFolder: googleDriveMainFolder
                        });
                        
                        if (result) {
                          setGoogleDriveSuccess('Settings saved successfully');
                        } else {
                          setGoogleDriveError('Failed to save settings');
                        }
                      } catch (error) {
                        console.error('Error saving Google Drive settings:', error);
                        setGoogleDriveError('Error saving settings: ' + error.message);
                      } finally {
                        setGoogleDriveLoading(false);
                      }
                    }}
                  />
                  
                  {googleDriveEnabled && googleDriveConnected && (
                    <Stack vertical spacing="tight">
                      <TextField
                        label="Main Google Drive Folder"
                        value={googleDriveMainFolder}
                        onChange={(value) => {
                          setGoogleDriveMainFolder(value);
                          try {
                            setGoogleDriveLoading(true);
                            GoogleDriveService.saveSettings(user.uid, {
                              enabled: googleDriveEnabled,
                              folderNamingOption: googleDriveFolderOption,
                              mainFolder: value
                            }).then(result => {
                              if (result) {
                                setGoogleDriveSuccess('Settings saved successfully');
                              } else {
                                setGoogleDriveError('Failed to save settings');
                              }
                              setGoogleDriveLoading(false);
                            }).catch(error => {
                              console.error('Error saving Google Drive settings:', error);
                              setGoogleDriveError('Error saving settings: ' + error.message);
                              setGoogleDriveLoading(false);
                            });
                          } catch (error) {
                            console.error('Error saving Google Drive settings:', error);
                            setGoogleDriveError('Error saving settings: ' + error.message);
                            setGoogleDriveLoading(false);
                          }
                        }}
                        helpText="The main folder in Google Drive where all product image folders will be created"
                      />
                      
                      <Select
                        label="Folder Naming Option"
                        options={[
                          { label: 'Product Name', value: 'productName' },
                          { label: 'Product ID', value: 'productId' },
                          { label: 'Product Name and ID', value: 'productNameAndId' }
                        ]}
                        value={googleDriveFolderOption}
                        onChange={async (value) => {
                          setGoogleDriveFolderOption(value);
                          try {
                            setGoogleDriveLoading(true);
                            const result = await GoogleDriveService.saveSettings(user.uid, {
                              enabled: googleDriveEnabled,
                              folderNamingOption: value,
                              mainFolder: googleDriveMainFolder
                            });
                            
                            if (result) {
                              setGoogleDriveSuccess('Settings saved successfully');
                            } else {
                              setGoogleDriveError('Failed to save settings');
                            }
                          } catch (error) {
                            console.error('Error saving Google Drive settings:', error);
                            setGoogleDriveError('Error saving settings: ' + error.message);
                          } finally {
                            setGoogleDriveLoading(false);
                          }
                        }}
                        helpText="Choose how product folders will be named within the main folder"
                      />
                    </Stack>
                  )}
                  
                  <Stack distribution="trailing">
                    {!googleDriveConnected ? (
                      <Button
                        primary
                        onClick={async () => {
                          try {
                            setGoogleDriveLoading(true);
                            setGoogleDriveError(''); // Clear any previous errors
                            
                            console.log('Initializing Google Drive auth for user:', user.uid);
                            const result = await GoogleDriveService.initializeAuth(user.uid);
                            
                            if (result.success && result.authUrl) {
                              console.log('Opening OAuth popup with URL:', result.authUrl);
                              
                              // Store the current origin in localStorage so the popup can use it for postMessage
                              try {
                                localStorage.setItem('parentOrigin', window.location.origin);
                                console.log('Stored parent origin in localStorage:', window.location.origin);
                              } catch (e) {
                                console.warn('Could not store parent origin in localStorage:', e);
                              }
                              
                              // Open Google auth in a new window with specific features
                              console.log('Opening OAuth popup for Google Drive');
                              const authWindow = window.open(
                                result.authUrl, 
                                '_blank', 
                                'width=600,height=700,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no'
                              );
                              
                              // Store reference to auth window to close it later if needed
                              window.googleDriveAuthWindow = authWindow;
                              
                              // Check if popup was blocked
                              if (!authWindow) {
                                setGoogleDriveLoading(false);
                                setGoogleDriveError('Popup was blocked. Please allow popups for this site.');
                                setToastMessage({ content: 'Popup was blocked. Please allow popups for this site.', error: true });
                                setToastActive(true);
                                return;
                              }
                              
                              console.log('OAuth popup opened successfully');
                              
                              // Reset the timeout ref
                              popupTimeoutIdRef.current = null;
                              receivedResponseRef.current = false;
                              
                              // Define a global callback function that the popup can call directly
                              window.googleDriveCallback = function(data) {
                                console.log('googleDriveCallback called directly with data:', data);
                                receivedResponseRef.current = true;
                                
                                if (popupTimeoutIdRef.current) {
                                  clearTimeout(popupTimeoutIdRef.current);
                                  popupTimeoutIdRef.current = null;
                                }
                                
                                // Add debug info to localStorage as a breadcrumb
                                try {
                                  localStorage.setItem('googleDriveCallbackTriggered', 'true');
                                  localStorage.setItem('googleDriveCallbackTime', new Date().toISOString());
                                } catch (e) {
                                  console.warn('Failed to set localStorage debug info:', e);
                                }
                                
                                // Process the callback data
                                if (data && data.success) {
                                  console.log('Google Drive connected successfully via direct callback!');
                                  setGoogleDriveConnected(true);
                                  setGoogleDriveLoading(false);
                                  setGoogleDriveError('');
                                  refreshUserData();
                                  setToastMessage({ content: 'Google Drive connected successfully!', error: false });
                                  setToastActive(true);
                                  
                                  // Try to close the auth window if we have a reference to it
                                  try {
                                    if (window.googleDriveAuthWindow && !window.googleDriveAuthWindow.closed) {
                                      console.log('Closing Google Drive auth window from callback');
                                      window.googleDriveAuthWindow.close();
                                    }
                                  } catch (e) {
                                    console.log('Could not close Google Drive auth window:', e);
                                  }
                                } else {
                                  console.error('Google Drive connection failed via direct callback:', data);
                                  setGoogleDriveLoading(false);
                                  setGoogleDriveError('Failed to connect Google Drive: ' + (data?.errorMessage || 'Unknown error'));
                                  setToastMessage({ content: 'Failed to connect Google Drive: ' + (data?.errorMessage || 'Unknown error'), error: true });
                                  setToastActive(true);
                                }
                                
                                // Remove the event listener since we've received a response
                                window.removeEventListener('message', messageHandler);
                              };
                              
                              // Also expose the callback on document object for easier access
                              document.googleDriveCallback = window.googleDriveCallback;

                              // Add it to the window object with additional aliases in case the OAuth popup
                              // is having trouble finding the right function name
                              window.onGoogleDriveCallback = window.googleDriveCallback;
                              window.onGoogleDriveSuccess = window.googleDriveCallback;
                              window.onOAuthComplete = window.googleDriveCallback;
  
                              // Also make the callback directly accessible from the window object to
                              // work around cross-origin issues
                              window.__GoogleDriveCallbackAccess = {
                                callbackFunction: window.googleDriveCallback
                              };
                              
                              // Add a flag to track if we've received a response
                              let receivedResponse = false;
                              
                              // Set up event listener for messages from the popup
                              const messageHandler = (event) => {
                                console.log('Received message event:', event.origin, event.data ? event.data.type : 'no data');
                                
                                // Check if the message is from our Google Drive OAuth flow
                                if (event.data && (event.data.type === 'GOOGLE_DRIVE_CONNECTED' || event.data.type === 'GOOGLE_DRIVE_ERROR')) {
                                  console.log('Received valid message from popup:', event.data);
                                  console.log('Message source:', event.data.source || 'unknown');
                                  console.log('Message origin:', event.origin);
                                  
                                  // Mark that we've received a response
                                  receivedResponseRef.current = true;
                                  
                                  // Clear the timeout since we've received a response
                                  if (popupTimeoutIdRef.current) {
                                    console.log('Clearing timeout since we received a response');
                                    clearTimeout(popupTimeoutIdRef.current);
                                    popupTimeoutIdRef.current = null;
                                  }
                                  
                                  // Remove the event listener once we've received a response
                                  window.removeEventListener('message', messageHandler);
                                  
                                  // Try to close the auth window if we have a reference to it
                                  try {
                                    if (window.googleDriveAuthWindow && !window.googleDriveAuthWindow.closed) {
                                      console.log('Closing Google Drive auth window after receiving message');
                                      window.googleDriveAuthWindow.close();
                                    }
                                  } catch (e) {
                                    console.log('Could not close Google Drive auth window:', e);
                                  }
                                  
                                  if (event.data.type === 'GOOGLE_DRIVE_CONNECTED' && event.data.success) {
                                    console.log('Google Drive connected successfully!');
                                    // Google Drive connected successfully
                                    setGoogleDriveConnected(true);
                                    
                                    // Fetch user data from Firestore to get updated Google Drive settings
                                    // We need to use a separate function since we can't use await directly in this event handler
                                    const fetchUserData = () => {
                                      console.log('Fetching updated user data...');
                                      getDoc(doc(db, 'users', user.uid))
                                        .then(userDoc => {
                                          if (userDoc.exists()) {
                                            console.log('User document exists, checking for Google Drive data');
                                            if (userDoc.data().googleDrive && userDoc.data().googleDrive.tokens) {
                                              console.log('Google Drive tokens found in user document');
                                              // Update main folder name if available
                                              if (userDoc.data().googleDrive.mainFolder) {
                                                console.log('Setting main folder name:', userDoc.data().googleDrive.mainFolder);
                                                setGoogleDriveMainFolder(userDoc.data().googleDrive.mainFolder);
                                              }
                                              // Update enabled state
                                              if (userDoc.data().googleDrive.enabled !== undefined) {
                                                console.log('Setting enabled state:', userDoc.data().googleDrive.enabled);
                                                setGoogleDriveEnabled(userDoc.data().googleDrive.enabled);
                                              }
                                            } else {
                                              console.log('No Google Drive data found in user document');
                                            }
                                          } else {
                                            console.log('User document does not exist');
                                          }
                                        })
                                        .catch(error => {
                                          console.error('Error fetching user data:', error);
                                        });
                                    };
                                    
                                    fetchUserData();
                                    
                                    setToastMessage({ content: 'Google Drive connected successfully!', error: false });
                                    setToastActive(true);
                                  } else {
                                    // Error connecting to Google Drive
                                    let errorMessage = 'Error connecting to Google Drive';
                                    
                                    // If we have detailed error information, use it
                                    if (event.data.errorTitle) {
                                      errorMessage = `${event.data.errorTitle}: ${event.data.errorDetails || ''}`;
                                      console.error('Google Drive connection error:', {
                                        title: event.data.errorTitle,
                                        details: event.data.errorDetails
                                      });
                                    }
                                    
                                    setGoogleDriveError(errorMessage);
                                    setToastMessage({ content: 'Failed to connect Google Drive: ' + (event.data.errorTitle || 'Unknown error'), error: true });
                                    setToastActive(true);
                                  }
                                  
                                  setGoogleDriveLoading(false);
                                }
                              };
                              
                              // Add the event listener
                              console.log('Adding message event listener');
                              window.addEventListener('message', messageHandler);
                              
                              // Set up a localStorage check interval as a backup communication method
                              const localStorageCheckInterval = setInterval(() => {
                                try {
                                  const connected = localStorage.getItem('googleDriveConnected');
                                  const timestamp = localStorage.getItem('googleDriveConnectedTime');
                                  
                                  if (connected === 'true' && timestamp) {
                                    console.log('Found Google Drive connection in localStorage check interval:', timestamp);
                                    
                                    // Clear the interval since we found a successful connection
                                    clearInterval(localStorageCheckInterval);
                                    
                                    // Clear the timeout if it exists
                                    if (popupTimeoutIdRef.current) {
                                      clearTimeout(popupTimeoutIdRef.current);
                                      popupTimeoutIdRef.current = null;
                                    }
                                    
                                    // Clean up localStorage items
                                    localStorage.removeItem('googleDriveConnected');
                                    localStorage.removeItem('googleDriveConnectedTime');
                                    localStorage.removeItem('googleDriveConnectedSource');
                                    localStorage.removeItem('googleDriveConnectedUrl');
                                    
                                    // Update UI
                                    setGoogleDriveConnected(true);
                                    setGoogleDriveLoading(false);
                                    refreshUserData();
                                    
                                    // Show success message
                                    setToastMessage({ 
                                      content: 'Google Drive connected successfully!', 
                                      error: false 
                                    });
                                    setToastActive(true);
                                    
                                    // Try to close the popup if it's still open
                                    try {
                                      if (authWindow && !authWindow.closed) {
                                        authWindow.close();
                                      }
                                    } catch (e) {
                                      console.log('Could not close auth window, may already be closed');
                                    }
                                  }
                                } catch (e) {
                                  console.warn('Error in localStorage check interval:', e);
                                }
                              }, 1000); // Check every second
                              
                              // Clean up the interval after a reasonable timeout
                              setTimeout(() => {
                                clearInterval(localStorageCheckInterval);
                              }, 65000); // Stop checking after 65 seconds
                              
                              // We can't reliably check if the popup is closed due to COOP restrictions
                              // Instead, we'll rely on the timeout, message event, and localStorage to determine the state
                              console.log('Not using window.closed checks due to COOP restrictions');
                              
                              // Set a timeout to stop loading state if we don't get a response
                              popupTimeoutIdRef.current = setTimeout(() => {
                                console.log('Google Drive connection timeout - no response received');
                                
                                // Look for callback trigger marks in localStorage
                                const callbackTriggered = localStorage.getItem('googleDriveCallbackTriggered');
                                const callbackTime = localStorage.getItem('googleDriveCallbackTime');
                                
                                if (callbackTriggered === 'true') {
                                  console.log('Callback was triggered at:', callbackTime, 'but main flow did not complete');
                                }
                                
                                // Check localStorage as a fallback mechanism
                                try {
                                  const localStorageConnected = localStorage.getItem('googleDriveConnected');
                                  const localStorageTime = localStorage.getItem('googleDriveConnectedTime');
                                  const localStorageSource = localStorage.getItem('googleDriveConnectedSource');
                                  const localStorageUrl = localStorage.getItem('googleDriveConnectedUrl');
                                  
                                  console.log('Checking localStorage for Google Drive connection:', {
                                    connected: localStorageConnected,
                                    time: localStorageTime,
                                    source: localStorageSource,
                                    url: localStorageUrl
                                  });
                                  
                                  if (localStorageConnected === 'true' && localStorageTime) {
                                    console.log('Found Google Drive connection in localStorage:', localStorageTime);
                                    console.log('Connection source:', localStorageSource || 'unknown');
                                    console.log('Connection URL:', localStorageUrl || 'unknown');
                                    receivedResponseRef.current = true;
                                    
                                    // Clear the localStorage items after reading them
                                    localStorage.removeItem('googleDriveConnected');
                                    localStorage.removeItem('googleDriveConnectedTime');
                                    localStorage.removeItem('googleDriveConnectedSource');
                                    localStorage.removeItem('googleDriveConnectedUrl');
                                    
                                    // Update the UI
                                    setGoogleDriveConnected(true);
                                    refreshUserData();
                                    setToastMessage({ content: 'Google Drive connected successfully via localStorage fallback!', error: false });
                                    setToastActive(true);
                                    setGoogleDriveLoading(false);
                                    window.removeEventListener('message', messageHandler);
                                    return;
                                  }
                                } catch (e) {
                                  console.warn('Error checking localStorage:', e);
                                }
                                
                                // Only proceed if we haven't received a response yet
                                if (!receivedResponseRef.current) {
                                  window.removeEventListener('message', messageHandler);
                                  setGoogleDriveLoading(false);
                                  
                                  // Check if the user completed the OAuth flow but the message didn't get through
                                  console.log('Checking if OAuth was completed despite no message received...');
                                  
                                  // Try to refresh user data anyway to see if tokens were saved
                                  refreshUserData();
                                  
                                  // Check if we can find any URL parameters in the current URL that indicate success
                                  const currentParams = new URLSearchParams(window.location.search);
                                  const urlConnected = currentParams.get('googleDriveConnected');
                                  
                                  if (urlConnected === 'true') {
                                    console.log('Found success parameter in URL despite timeout');
                                    setGoogleDriveConnected(true);
                                    setToastMessage({ 
                                      content: 'Google Drive connected successfully (detected after timeout)!', 
                                      error: false 
                                    });
                                  } else {
                                    setGoogleDriveError('No response received from the authentication window. Please check if Google Drive was connected in your account settings.');
                                    setToastMessage({ 
                                      content: 'Authentication may have completed, but no confirmation was received. Please check your Google Drive connection status.', 
                                      error: true 
                                    });
                                  }
                                  setToastActive(true);
                                  
                                  // Try to close the popup if it's still open, but this may fail due to COOP
                                  try {
                                    authWindow.close();
                                  } catch (e) {
                                    console.log('Could not close auth window due to COOP restrictions');
                                  }
                                }
                              }, 60000); // 1 minute timeout
                            } else {
                              setGoogleDriveLoading(false);
                              setGoogleDriveError('Failed to get OAuth URL');
                              setToastMessage({ content: 'Failed to get OAuth URL', error: true });
                              setToastActive(true);
                            }
                          } catch (error) {
                            console.error('Error connecting to Google Drive:', error);
                            setGoogleDriveLoading(false);
                            setGoogleDriveError('Error connecting to Google Drive: ' + error.message);
                            setToastMessage({ content: 'Error connecting to Google Drive: ' + error.message, error: true });
                            setToastActive(true);
                          }
                        }}
                      >
                        Connect Google Drive
                      </Button>
                    ) : (
                      <Button
                        destructive
                        onClick={async () => {
                          try {
                            setGoogleDriveLoading(true);
                            const result = await GoogleDriveService.revokeAccess(user.uid);
                            
                            if (result) {
                              setGoogleDriveConnected(false);
                              setGoogleDriveEnabled(false);
                              setGoogleDriveSuccess('Google Drive disconnected successfully');
                            } else {
                              setGoogleDriveError('Failed to disconnect Google Drive');
                            }
                          } catch (error) {
                            console.error('Error disconnecting Google Drive:', error);
                            setGoogleDriveError('Error disconnecting Google Drive: ' + error.message);
                          } finally {
                            setGoogleDriveLoading(false);
                          }
                        }}
                      >
                        Disconnect Google Drive
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Card.Section>
            </Card>
          )}
          {selectedTab === 4 && (
            <Card title="Report a Bug" sectioned>
              <ClickUpForm />
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Full Name Update Modal */}
      <Modal
        open={showFullNameModal}
        onClose={() => {
          setShowFullNameModal(false);
          setError('');
          // Reset to current value
          setNewFullName(userData?.fullName || '');
        }}
        title="Update Full Name"
        primaryAction={{
          content: 'Update',
          onAction: handleUpdateFullName,
          loading: loading,
          disabled: !newFullName.trim() || loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowFullNameModal(false);
              setError('');
              // Reset to current value
              setNewFullName(userData?.fullName || '');
            }
          }
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <FormLayout>
            <TextField
              label="Full Name"
              value={newFullName}
              onChange={setNewFullName}
              autoComplete="name"
              disabled={loading}
              required
              helpText="Enter your full name as you would like it to appear"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setError('');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }}
        title="Change Password"
        primaryAction={{
          content: 'Change Password',
          onAction: handleChangePassword,
          loading: loading,
          disabled: !currentPassword || !newPassword || !confirmPassword || loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowPasswordModal(false);
              setError('');
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }
          }
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <FormLayout>
            <TextField
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              error={error && error.includes('current password') ? error : undefined}
              disabled={loading}
              required
            />
            <TextField
              type="password"
              label="New Password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              error={error && error.includes('new password') ? error : undefined}
              helpText="Password must be at least 6 characters long"
              disabled={loading}
              required
            />
            <TextField
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              error={newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
              disabled={loading}
              required
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={showPaypalModal}
        onClose={() => {
          setShowPaypalModal(false);
          setError('');
          setPaypalEmail(referralInfo?.paypalEmail || '');
        }}
        title="Update PayPal Email"
        primaryAction={{
          content: 'Update',
          onAction: handleUpdatePaypalEmail,
          loading: loading,
          disabled: !paypalEmail || !paypalEmail.includes('@') || loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowPaypalModal(false);
              setError('');
              setPaypalEmail(referralInfo?.paypalEmail || '');
            }
          }
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <FormLayout>
            <TextField
              label="PayPal Email"
              value={paypalEmail}
              onChange={setPaypalEmail}
              autoComplete="email"
              disabled={loading}
              required
              helpText="Enter your PayPal email address to receive payouts"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={showReferralInfoModal}
        onClose={() => {
          setShowReferralInfoModal(false);
          setError('');
        }}
        title="🤝 Join Our Referral Program"
        primaryAction={{
          content: 'Apply Now',
          onAction: () => window.location.href = 'mailto:referral@elevia-solutions.com?subject=Referral%20Partner%20Application',
        }}
        secondaryActions={[]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <div style={{ padding: '10px 0' }}>
            <TextContainer>
              <Text variant="bodyLg" as="p">
                Earn money by sharing what you love.
              </Text>
              <Text variant="bodyMd" as="p" color="subdued">
                Do you have an engaged audience and know how to turn reach into results? Then our referral program is for you!
              </Text>
              
              <Text variant="headingMd" as="h3" fontWeight="bold">
                📌 What You Need to Know:
              </Text>
              
              <Text variant="headingSm" as="h4" fontWeight="medium">
                Who we're looking for:
              </Text>
              <Text variant="bodyMd" as="p">
                Creators, influencers, or networkers with a solid follower base and the ability to attract real customers.
              </Text>
              
              <Text variant="headingSm" as="h4" fontWeight="medium">
                Payout model:
              </Text>
              <Text variant="bodyMd" as="p">
                You earn a commission for every successful subscription you refer.
                Payouts are made 30 days after a user subscribes — this delay allows time for refunds or cancellations.
              </Text>
              
              <Text variant="headingSm" as="h4" fontWeight="medium">
                Referral dashboard:
              </Text>
              <Text variant="bodyMd" as="p">
                Once approved, you'll get access to a dashboard right on this page where you can:
              </Text>
              <List type="bullet">
                <List.Item>See who you've referred</List.Item>
                <List.Item>Track active subscriptions</List.Item>
                <List.Item>Monitor open and completed payouts</List.Item>
              </List>
              
              <Text variant="headingMd" as="h3" fontWeight="bold" color="success">
                🚀 Ready to Get Started?
              </Text>
              <Text variant="bodyMd" as="p">
                Tap the button below to apply – this will open your email program with a pre-filled message.
              </Text>
            </TextContainer>
          </div>
        </Modal.Section>
      </Modal>
      
      {toastActive && (
        <Toast 
          content={toastMessage.content}
          error={toastMessage.error}
          onDismiss={() => setToastActive(false)}
          duration={4500}
        />
      )}
    </Page>
  );
}
